import React, { Dispatch, SetStateAction, useState } from 'react';

import { formatMoney, toMoney } from '@betterment/core';
import { useMutation, useQuery } from '@betterment/js-runtime';
import { Box, Button, Flex, Text } from '@betterment/style-closet-react';

import { AcatsModal } from './AcatsModal';
import Layout, { StyledCheckbox } from './Layout';
import LinkSent from './LinkSent';

import { CoPilotSectionProvider, useCoPilotSectionState } from '../../contexts/CoPilotSectionContext';
import { Acat } from '../../services/api/types/co_pilot/acats';
import { ActionRequestParams, CoPilotResponseMeta } from '../../utils/co_pilot';

const ApprovalAcatsRow: React.FC<
  React.PropsWithChildren<{
    approvalAcat: Acat;
    setCurrentModalAcat: Dispatch<SetStateAction<Acat | null>>;
  }>
> = ({ approvalAcat, setCurrentModalAcat }) => {
  const { isItemSelected, handleSelectedItemChange } = useCoPilotSectionState<Acat>();
  const { id, clientName, amount, linkSentAt, queuedAt } = approvalAcat;

  return (
    <tr>
      <td>
        <Flex alignItems="center">
          <StyledCheckbox
            name={id}
            id={id}
            role="checkbox"
            type="checkbox"
            className="checkboxInputBase"
            checked={isItemSelected(id)}
            onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => handleSelectedItemChange(target.id)}
          />
          {clientName}
        </Flex>
      </td>
      <td>{formatMoney(toMoney(amount))}</td>
      <td>
        Pending |
        <Text as="a" color="bmtBlue" onClick={() => setCurrentModalAcat(approvalAcat)} pl={2}>
          View
        </Text>
      </td>
      <LinkSent linkSentAt={linkSentAt} queuedAt={queuedAt} />
    </tr>
  );
};

const AcatsApprovalsTable: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { itemList, status, handleSelectAllChange, lastPage, loadMore } = useCoPilotSectionState<Acat>();
  const [currentModalAcat, setCurrentModalAcat] = useState<Acat | null>(null);

  return (
    <>
      {currentModalAcat && (
        <AcatsModal acat={currentModalAcat} closeHandler={() => setCurrentModalAcat(null)} status="pending" />
      )}
      <table>
        <tbody>
          <tr>
            <Text as="th" fontWeight="medium" pb={6}>
              <Flex alignItems="center">
                <StyledCheckbox
                  name="selectAll"
                  id="selectAll"
                  role="checkbox"
                  type="checkbox"
                  className="checkboxInputBase"
                  checked={status === 'allSelected'}
                  onChange={() => handleSelectAllChange()}
                />
                Client
              </Flex>
            </Text>
            <Text as="th" fontWeight="medium" pb={6}>
              Amount
            </Text>
            <Text as="th" fontWeight="medium" pb={6}>
              Status
            </Text>
            <Text as="th" fontWeight="medium" pb={6}>
              Link sent
            </Text>
          </tr>
          {itemList?.map(approvalAcat => (
            <ApprovalAcatsRow
              key={approvalAcat.id}
              approvalAcat={approvalAcat}
              setCurrentModalAcat={setCurrentModalAcat}
            />
          ))}
          {status !== 'empty' && status !== 'fetchError' && !lastPage && (
            <tr>
              <td>
                <Box pb={9}>
                  <Button variant="tertiary" onClick={() => loadMore()}>
                    Load more
                  </Button>
                </Box>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
};

const AcatsApprovals: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { refetch } = useQuery<Acat[], CoPilotResponseMeta>(`/api/v1/co_pilot/approval_acats_transfer_updates`, {
    skip: true,
  });
  const [postDismissal] = useMutation<{}, ActionRequestParams>(
    '/api/v1/co_pilot/approval_acats_transfer_updates/dismissals'
  );
  const [postSendReminder] = useMutation<{}, ActionRequestParams>(
    '/api/v1/co_pilot/approval_acats_transfer_updates/reminders'
  );

  return (
    <CoPilotSectionProvider
      fetch={refetch}
      initialOrder={[{ col: 'client_name', dir: 'desc' }]}
      postHandleDismissal={postDismissal}
      postSendReminder={postSendReminder}
    >
      <Layout
        activeTab="ACATS Approvals"
        header="These clients have pending ACATS transfers that you initiated."
        subHeader="Send a reminder email to clients who have not yet approved their transfers."
      >
        <AcatsApprovalsTable />
      </Layout>
    </CoPilotSectionProvider>
  );
};

export default AcatsApprovals;
